import { EnumCommodity } from '../../api'
import {
  apiCreateConsumptionQuantityByProfile,
  apiDeleteLeadByID,
  apiEvaluateLead,
  apiGetLeadAnnouncements,
  apiGetLeadByID,
  apiGetLeads,
  apiGetLeadsByFilter,
  apiGetLeadsByType,
  apiGetObjectionsByLeadId,
  apiPostLead,
  apiPostQueryLead,
  apiUpdateLead
} from '../../api/Argentia/endpoints/leads/leads.api'
import {
  EvaluateLeadRequestModel,
  LeadAnnouncementResponseModel,
  LeadConsumptionQuantityByProfileRequestModel,
  LeadModel,
  LeadQueryModel,
  LeadQueryRequestModel,
  LeadsSearchFilterModel
} from '../../api/Argentia/endpoints/leads/model/leads'
import { EnumTipoLead } from '../../api/oData/enums/enumTipoLead'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import { ServiceResponse } from '../../model/serviceResponse'
import { ObjectionTableModel } from '../invoicesPage/invoicesPage.Model'
import { mapServiceResponseToLeadModel } from './leads.mapper'
import { LeadRequestModel } from './leads.model'

export const getLeadById = async (
  leadID: number
): Promise<ServiceResponse<LeadModel | undefined>> => {
  return await apiGetLeadByID(leadID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getLeads = async (
  commodity: EnumCommodity
): Promise<ServiceResponse<LeadModel[] | undefined>> => {
  return await apiGetLeads(commodity)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchLeads = async (
  commodity: EnumCommodity,
  leadType: EnumTipoLead
): Promise<ServiceResponse<LeadModel[] | undefined>> => {
  return await apiGetLeadsByType(commodity, leadType)
    .then(response => {
      return createSuccessServiceResponse(mapServiceResponseToLeadModel(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getFilteredLeads = async (
  searchFilter: LeadsSearchFilterModel
): Promise<ServiceResponse<LeadModel[] | undefined>> => {
  return await apiGetLeadsByFilter(searchFilter)
    .then(response => {
      return createSuccessServiceResponse(mapServiceResponseToLeadModel(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const saveLead = async (
  request: LeadRequestModel
): Promise<ServiceResponse<LeadModel | undefined>> => {
  return await apiPostLead(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateLead = async (
  request: LeadRequestModel,
  leadId: number
): Promise<ServiceResponse<LeadModel | undefined>> => {
  return await apiUpdateLead(request, leadId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const queryLead = async (
  request: LeadQueryRequestModel
): Promise<ServiceResponse<LeadQueryModel[] | undefined>> => {
  return await apiPostQueryLead(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getLeadAnnouncements = async (
  commodity: EnumCommodity,
  leadType: EnumTipoLead
): Promise<ServiceResponse<LeadAnnouncementResponseModel[] | undefined>> => {
  return await apiGetLeadAnnouncements(commodity, leadType)
    .then(response => {
      return createSuccessServiceResponse(
        mapServiceResponseToLeadModel(response) as LeadAnnouncementResponseModel[]
      )
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const evaluateLead = async (
  request: EvaluateLeadRequestModel[]
): Promise<ServiceResponse<any | undefined>> => {
  return await apiEvaluateLead(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createConsumptionQuantityByProfile = async (
  request: LeadConsumptionQuantityByProfileRequestModel
): Promise<ServiceResponse<LeadModel | undefined>> => {
  return await apiCreateConsumptionQuantityByProfile(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getObjectionsByLeadId = async (
  leadId: number
): Promise<ServiceResponse<ObjectionTableModel[] | undefined>> => {
  return await apiGetObjectionsByLeadId(leadId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deleteLeadById = async (
  leadID: number
): Promise<ServiceResponse<LeadModel | undefined>> => {
  return await apiDeleteLeadByID(leadID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkLeadCodeExists = async (
  code: string,
  commodity: EnumCommodity,
  leadType: EnumTipoLead,
  excludeLeadId?: number
): Promise<ServiceResponse<boolean>> => {
  const response = await getFilteredLeads({ code, commodity, leadType })
  if (!response.success) {
    return { success: false, error: response.error, data: undefined }
  }
  const exists = (response.data ?? []).some(
    lead =>
      lead.code.toLowerCase() === code.toLowerCase() &&
      (!excludeLeadId || lead.id !== excludeLeadId)
  )
  return createSuccessServiceResponse(exists)
}
